import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CollapseSection from "../../../components/ui/CollapseSection";
import {
   getModificationSkuRows,
   getProductGroupDetailRecord,
} from "./cgpgUtils";

function formatValidityPeriod(startDate, endDate) {
   if (!startDate || !endDate) {
      return "";
   }

   const formatDate = (value) =>
      new Date(value).toLocaleDateString("en-UK", {
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
      });

   return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

function getSkuColor(isActive) {
   if (isActive === 1) {
      return "#008000";
   }

   if (isActive === -1) {
      return "#C00000";
   }

   return "#000000";
}

function filterSkuRows(skuRows, skuFilter) {
   if (skuFilter === "added") {
      return skuRows.filter((item) => item.isActive === 1);
   }

   if (skuFilter === "deleted") {
      return skuRows.filter((item) => item.isActive === -1);
   }

   return skuRows;
}

export default function ProductGroupSummary() {
   const { groupRowClicked, selectedView, pG_Details, pgModificationDetails } =
      useSelector((store) => store.cgpgApproval);
   const productGroupDetail = getProductGroupDetailRecord(pG_Details);
   const [skuRows, setSkuRows] = useState([]);
   const [skuFilter, setSkuFilter] = useState("all");
   const [isAscByName, setAscByName] = useState(false);
   const [isAscByNumber, setAscByNumber] = useState(false);

   useEffect(() => {
      setSkuRows(getModificationSkuRows(pgModificationDetails));
      setSkuFilter("all");
      setAscByName(false);
      setAscByNumber(false);
   }, [pgModificationDetails]);

   const displayedSkuRows = useMemo(
      () => filterSkuRows(skuRows, skuFilter),
      [skuRows, skuFilter]
   );

   if (!groupRowClicked || selectedView !== "Product Group") {
      return null;
   }

   const handleSkuSort = (type) => {
      if (type === "name") {
         const list = [...skuRows].sort((a, b) => {
            const comparison = a.sku.toLowerCase().trim().localeCompare(
               b.sku.toLowerCase().trim()
            );
            return isAscByName ? -comparison : comparison;
         });

         setSkuRows(list);
         setAscByName(!isAscByName);
         return;
      }

      const list = [...skuRows].sort((a, b) => {
         const comparison = String(a.skuNumber ?? "").localeCompare(
            String(b.skuNumber ?? ""),
            undefined,
            { numeric: true, sensitivity: "base" }
         );
         return isAscByNumber ? -comparison : comparison;
      });

      setSkuRows(list);
      setAscByNumber(!isAscByNumber);
   };

   return (
      <Box sx={{ mt: 2 }}>
         <CollapseSection title="Summary">
            <Box
               sx={{
                  fontSize: "14px",
                  ml: 2,
                  mt: 2,
               }}
            >
               <LabelValue
                  label="Product Group"
                  value={productGroupDetail?.packGroupName ?? ""}
               />
               <LabelValue
                  label="Validity Period"
                  value={formatValidityPeriod(
                     productGroupDetail?.effectiveStartDate,
                     productGroupDetail?.effectiveEndDate
                  )}
               />
               <Box>
                  <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                     SKU(s):{" "}
                  </Typography>
                  <Box
                     sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                        mb: 2,
                     }}
                  >
                     <Typography
                        sx={{ fontSize: "14px", cursor: "pointer" }}
                        onClick={() => handleSkuSort("name")}
                        display="inline"
                     >
                        Sort by Name |
                     </Typography>
                     <Typography
                        sx={{ fontSize: "14px", cursor: "pointer" }}
                        onClick={() => handleSkuSort("number")}
                        display="inline"
                     >
                        Sort by Number
                     </Typography>
                     <Button
                        variant={
                           skuFilter === "added" ? "contained" : "outlined"
                        }
                        onClick={() => setSkuFilter("added")}
                        sx={{
                           textTransform: "none",
                           boxShadow: "none",
                           ...(skuFilter === "added"
                              ? {
                                   backgroundColor: "#008000",
                                   color: "#FFFFFF",
                                   "&:hover": {
                                      backgroundColor: "#006400",
                                      boxShadow: "none",
                                   },
                                }
                              : {
                                   borderColor: "#008000",
                                   color: "#008000",
                                   backgroundColor: "#FFFFFF",
                                   "&:hover": {
                                      borderColor: "#008000",
                                      backgroundColor:
                                         "rgba(0, 128, 0, 0.06)",
                                      boxShadow: "none",
                                   },
                                }),
                        }}
                     >
                        Added SKUs
                     </Button>
                     <Button
                        variant={
                           skuFilter === "deleted" ? "contained" : "outlined"
                        }
                        onClick={() => setSkuFilter("deleted")}
                        sx={{
                           textTransform: "none",
                           boxShadow: "none",
                           ...(skuFilter === "deleted"
                              ? {
                                   backgroundColor: "#C00000",
                                   color: "#FFFFFF",
                                   "&:hover": {
                                      backgroundColor: "#A00000",
                                      boxShadow: "none",
                                   },
                                }
                              : {
                                   borderColor: "#C00000",
                                   color: "#C00000",
                                   backgroundColor: "#FFFFFF",
                                   "&:hover": {
                                      borderColor: "#C00000",
                                      backgroundColor:
                                         "rgba(192, 0, 0, 0.06)",
                                      boxShadow: "none",
                                   },
                                }),
                        }}
                     >
                        Deleted SKUs
                     </Button>
                     <Button
                        variant="outlined"
                        onClick={() => setSkuFilter("all")}
                        sx={{
                           textTransform: "none",
                           borderColor: "#BDBDBD",
                           color: "#000000",
                           backgroundColor: "#FFFFFF",
                           boxShadow: "none",
                           "&:hover": {
                              borderColor: "#BDBDBD",
                              backgroundColor: "#FFFFFF",
                              boxShadow: "none",
                           },
                        }}
                     >
                        Clear Filter
                     </Button>
                  </Box>
                  <Box
                     sx={{
                        border: "1px solid #C4C4C4",
                        borderRadius: 1,
                        minHeight: 180,
                        maxHeight: 180,
                        overflowY: "auto",
                        p: 1.5,
                        fontSize: "14px",
                        fontWeight: 400,
                     }}
                  >
                     {displayedSkuRows.map((item) => (
                        <Box
                           key={`${item.id}-${item.sku}`}
                           sx={{
                              color: getSkuColor(item.isActive),
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.5,
                           }}
                        >
                           {item.sku}
                        </Box>
                     ))}
                  </Box>
               </Box>
            </Box>
         </CollapseSection>
      </Box>
   );
}

function LabelValue({ label, value }) {
   return (
      <div>
         <strong>{label}:</strong>&nbsp;{value}
      </div>
   );
}
